import React from 'react';
import { contactSchema } from './schemas';
import { Formik, Form } from 'formik';
import { Box, Stack, Heading, Container, Flex, Spacer, useDisclosure, Collapse, Button, Divider, Icon } from '@chakra-ui/react';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { languagesEN, titlesEN } from '../../../shared/components/functions';
import { useLookupStore } from '../../../context/useStore';
import { TextInput, SelectInput, TextareaInput, CheckboxInput, BusinessTypeCheckboxInput, fairOutlineButtonProps } from './forms';

export default function ContactForms({ handleSubmit, SubmitButton, prefillData }) {
  const { languages, contacttitels } = useLookupStore();
  const langEN = languagesEN(languages);
  const titles = titlesEN(contacttitels);

  const { isOpen, onToggle, onOpen } = useDisclosure();

  let allValues = {
    NAAM: '',
    DEPT: '',
    AanspreektitelID: '1',
    TaalID: '3',
    'E-mail': '',
    GSM: '',
    TEL: '',
    OPMERKING: '',
    interestedIn: [],
    businessType: [],
    otherBusinessType: '',
    talkedTo: ''
  };

  return (
    <Formik initialValues={allValues} validationSchema={contactSchema} onSubmit={(values) => handleSubmit(values)}>
      {(formikProps) => {
        const { setValues } = formikProps;

        React.useEffect(() => {
          if (!prefillData) return;

          setValues((currentValues) => {
            const nextValues = { ...currentValues };

            const assignIfPresent = (field, value) => {
              if (value === undefined || value === null) return;

              if (Array.isArray(value)) {
                nextValues[field] = value;
                return;
              }

              if (typeof value === 'string') {
                const trimmed = value.trim();
                if (trimmed !== '') {
                  nextValues[field] = trimmed;
                }
                return;
              }

              nextValues[field] = value;
            };

            const fieldsToAssign = ['NAAM', 'DEPT', 'AanspreektitelID', 'TaalID', 'E-mail', 'GSM', 'TEL', 'OPMERKING', 'interestedIn', 'businessType', 'otherBusinessType', 'talkedTo'];

            fieldsToAssign.forEach((field) => assignIfPresent(field, prefillData[field]));

            return nextValues;
          });

          const shouldOpen = [prefillData.DEPT, prefillData.GSM, prefillData.TEL, prefillData.OPMERKING, prefillData.businessType, prefillData.interestedIn, prefillData.otherBusinessType].some(
            (value) => {
              if (Array.isArray(value)) return value.length > 0;
              return typeof value === 'string' ? value.trim() !== '' : Boolean(value);
            }
          );

          if (shouldOpen) {
            onOpen();
          }
        }, [prefillData, onOpen, setValues]);

        return (
          <Form>
            <Container py={{ base: '4', md: '8' }} px={{ base: '4', md: '8' }}>
              <Stack spacing="5">
                <Heading size="xl">General information</Heading>
              </Stack>

              <Stack direction={{ base: 'column', lg: 'row' }} spacing={{ base: '5', lg: '8' }} justify="space-between">
                <Box bg="bg-surface" borderRadius="lg" flex="1">
                  <Stack spacing="5" py={{ base: '5', md: '6' }}>
                    <TextInput isRequired aria-required="true" autoFocus label="Your name (first and last name)" name="NAAM" type="text" />
                    <TextInput isRequired aria-required="true" label="E-Mail" name="E-mail" type="text" />
                    <CheckboxInput
                      label="Interested in"
                      name="interestedIn"
                      handleChange={(e) => {
                        if (e.target.checked) {
                          formikProps.setFieldValue('interestedIn', [...formikProps.values.interestedIn, e.target.id]);
                        } else {
                          formikProps.setFieldValue(
                            'interestedIn',
                            formikProps.values.interestedIn.filter((i) => i !== e.target.id)
                          );
                        }
                      }}
                    />

                    <Divider my={4} />

                    <BusinessTypeCheckboxInput
                      label="Type of business"
                      name="businessType"
                      handleChange={(e) => {
                        if (e.target.checked) {
                          formikProps.setFieldValue('businessType', [...formikProps.values.businessType, e.target.id]);
                        } else {
                          formikProps.setFieldValue(
                            'businessType',
                            formikProps.values.businessType.filter((i) => i !== e.target.id)
                          );
                        }
                      }}
                      setOtherValue={(value) => {
                        formikProps.setFieldValue('otherBusinessType', value);
                      }}
                    />

                    <Divider my={4} />

                    <SelectInput
                      label="I have talked to"
                      name="talkedTo"
                      as="select"
                      options={[
                        <option key="" value="">
                          Select a person...
                        </option>,
                        <option key="Jan" value="Jan">
                          Jan
                        </option>,
                        <option key="Stephen" value="Stephen">
                          Stephen
                        </option>,
                        <option key="Kim" value="Kim">
                          Kim
                        </option>,
                        <option key="Joren" value="Joren">
                          Joren
                        </option>,
                        <option key="Nelke" value="Nelke">
                          Nelke
                        </option>,
                        <option key="Matthias" value="Matthias">
                          Matthias
                        </option>,
                        <option key="Ingrid" value="Ingrid">
                          Ingrid
                        </option>
                      ]}
                    />

                    <Divider my={4} />

                    <TextareaInput label="Note" name="OPMERKING" type="text" />
                  </Stack>
                </Box>
              </Stack>
            </Container>

            <Container py={{ base: '4', md: '8' }} px={{ base: '4', md: '8' }}>
              <Flex spacing="5" direction="row" verticalAlign="center">
                <Heading size="xl">Some more information about you</Heading>
                <Spacer />
                <Button onClick={onToggle} aria-label={isOpen ? 'Hide more information' : 'Show more information'} {...fairOutlineButtonProps}>
                  <Icon as={isOpen ? FiChevronUp : FiChevronDown} />
                </Button>
              </Flex>

              <Collapse in={isOpen} animateOpacity>
                <Stack direction={{ base: 'column', lg: 'row' }} spacing={{ base: '5', lg: '8' }} justify="space-between">
                  <Box bg="bg-surface" borderRadius="lg" flex="1">
                    <Stack spacing="5" py={{ base: '5', md: '6' }}>
                      <TextInput label="Function" name="DEPT" type="text" />
                      <Stack spacing="6" direction={{ base: 'column', md: 'row' }}>
                        <SelectInput label="Title" autoFocus={isOpen} name="AanspreektitelID" as="select" options={titles} />
                        <SelectInput label="Language" name="TaalID" as="select" options={langEN} />
                      </Stack>
                      <Stack spacing="6" direction={{ base: 'column', md: 'row' }}>
                        <TextInput label="Phone" name="GSM" type="text" />
                        <TextInput label="Mobile phone" name="TEL" type="text" />
                      </Stack>
                      <Stack spacing="6" direction={{ base: 'column', md: 'row' }}>
                        <TextareaInput label="Note" name="OPMERKING" type="text" />
                      </Stack>
                    </Stack>
                  </Box>
                </Stack>
              </Collapse>
            </Container>
            {SubmitButton}
          </Form>
        );
      }}
    </Formik>
  );
}
